import { CalendarFilled } from '@calendly/ui/components/glyphs/CalendarFilled'
import { CalendlyContacts } from '@calendly/ui/components/glyphs/CalendlyContacts'
import { CalendlyContactsFilled } from '@calendly/ui/components/glyphs/CalendlyContactsFilled'
import { CalendlyNotetaker } from '@calendly/ui/components/glyphs/CalendlyNotetaker'
import { CalendlyNotetakerFilled } from '@calendly/ui/components/glyphs/CalendlyNotetakerFilled'
import { CalendlyPayments } from '@calendly/ui/components/glyphs/CalendlyPayments'
import { CalendlyPaymentsFilled } from '@calendly/ui/components/glyphs/CalendlyPaymentsFilled'
import { CalendlyScheduling } from '@calendly/ui/components/glyphs/CalendlyScheduling'
import { CalendlySchedulingFilled } from '@calendly/ui/components/glyphs/CalendlySchedulingFilled'
import { IconCalendar } from '@calendly/ui/components/glyphs/IconCalendar'
import { IconClipboardCheck } from '@calendly/ui/components/glyphs/IconClipboardCheck'
import { IconClipboardCheckFilled } from '@calendly/ui/components/glyphs/IconClipboardCheckFilled'
import { IconSparkles2 } from '@calendly/ui/components/glyphs/IconSparkles2'
import { IconSparkles2Filled } from '@calendly/ui/components/glyphs/IconSparkles2Filled'
import { IconWindmill } from '@calendly/ui/components/glyphs/IconWindmill'
import { WindmillRotated } from '@calendly/ui/components/glyphs/WindmillRotated'
import type { ComponentType, ReactNode, SVGProps } from 'react'

/** SVG icon component type used for nav idle/selected glyph pairs. */
export type GlyphComponent = ComponentType<SVGProps<SVGSVGElement>>

/**
 * Icon lookup by menu item tag. The legacy navigation config gives every primary link a short fixed name
 * (like `scheduling` or `contacts-link`). Product analytics rely on that name to tell which menu item was used.
 * This table maps each of those names to the idle and selected icons for this rail only.
 */
const NAV_ICON_PAIR: Record<string, { iconIdle: GlyphComponent; iconSelected: GlyphComponent }> = {
  'ai-link': { iconIdle: IconSparkles2 as GlyphComponent, iconSelected: IconSparkles2Filled as GlyphComponent },
  'todays-focus-link': { iconIdle: IconClipboardCheck as GlyphComponent, iconSelected: IconClipboardCheckFilled as GlyphComponent },
  scheduling: { iconIdle: CalendlyScheduling as GlyphComponent, iconSelected: CalendlySchedulingFilled as GlyphComponent },
  'scheduled-events-link': { iconIdle: IconCalendar as GlyphComponent, iconSelected: CalendarFilled as GlyphComponent },
  'recaps-link': { iconIdle: CalendlyNotetaker as GlyphComponent, iconSelected: CalendlyNotetakerFilled as GlyphComponent },
  'contacts-link': { iconIdle: CalendlyContacts as GlyphComponent, iconSelected: CalendlyContactsFilled as GlyphComponent },
  'sell-link': { iconIdle: CalendlyPayments as GlyphComponent, iconSelected: CalendlyPaymentsFilled as GlyphComponent },
  'workflows-link': { iconIdle: IconWindmill as GlyphComponent, iconSelected: WindmillRotated as GlyphComponent },
}

/** One rail row: targets and routing metadata from the legacy navigation tab config, plus shell-specific label and icons. */
export type ShellNavRowDef = {
  shouldShow: boolean
  href: string
  label: string
  activePaths?: string[]
  activePathPrefixes?: string[]
  suffix?: ReactNode
  /** Short fixed name for this link from the legacy nav config; analytics use it, and this file uses it to pick icons. */
  dataCalendlyLabel?: string
  pendoId?: string
  iconIdle?: GlyphComponent
  iconSelected?: GlyphComponent
}

/**
 * Looks up idle and selected icons from each row’s short analytics name (`dataCalendlyLabel`).
 * If the legacy config adds a new primary link, its name must be added to `NAV_ICON_PAIR` or this throws when rendering.
 *
 * @param dataCalendlyLabel - That name for one link (usually words separated by hyphens, e.g. `scheduled-events-link`).
 */
export function iconsForCalendlyNavLabel(dataCalendlyLabel: string): Pick<ShellNavRowDef, 'iconIdle' | 'iconSelected'> {
  const pair = NAV_ICON_PAIR[dataCalendlyLabel]
  if (!pair) {
    throw new Error(
      `AppShellVerticalNav: add "${dataCalendlyLabel}" to NAV_ICON_PAIR (no placeholder icons).`
    )
  }
  return pair
}
